import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { GET_VERIFICATION_SERVICES, get_request_config } from '@/utils/system_constants/constant';
import { mapTenantIntegrations } from '@/services/xverdict_config_mapper';
import {
  saveTenantVerificationServiceIds,
  selectedTenantVerificationServiceIds,
} from '@/services/tenant_verification_preferences';

const CONNECTED_INTEGRATIONS = [
  'saps_reference',
  'home_affairs',
  'whatsapp_business',
  'bank_eft',
];

function parse_json(value, fallback = {}) {
  try { return JSON.parse(value); }
  catch { return fallback; }
}

function response_collection(response) {
  const payload = response?.data ?? response ?? {};
  const parsed = typeof payload === 'string' ? parse_json(payload) : payload;
  const data = parsed?.data ?? parsed;
  const normalized = typeof data === 'string' ? parse_json(data) : data;
  return [normalized?.data, normalized?.results, normalized?.items, normalized].find(Array.isArray) || [];
}

function positive_number(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function api_error_message(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.detail || error?.message || fallback;
}

export default {
  name: 'TenantIntegrations',
  mixins: [alert_mixin],
  props: {
    projectId: {
      type: Number,
      default: null,
    },
  },
  data: () => ({
    connected_services: mapTenantIntegrations(CONNECTED_INTEGRATIONS).map((integration) => ({
      ...integration,
      name: integration.title,
      copy: integration.description,
    })),
    loading: false,
    services: [],
    selected_service_ids: [],
    integrations_error: '',
  }),
  mounted() {
    this.load_integrations();
  },
  methods: {
    service_is_selected(service = {}) {
      return this.selected_service_ids.includes(Number(service.id));
    },
    async load_integrations() {
      this.loading = true;
      this.integrations_error = '';
      this.selected_service_ids = selectedTenantVerificationServiceIds(this.projectId);

      try {
        const response = await axios.get(GET_VERIFICATION_SERVICES, get_request_config());
        this.services = response_collection(response)
          .map((service) => ({
            ...service,
            id: positive_number(service?.id || service?.service_id),
            name: String(service?.name || service?.service_name || service?.title || 'Verification service').trim(),
            description: String(service?.description || 'Verify submitted case information using this service.').trim(),
          }))
          .filter((service) => service.id);
      } catch (error) {
        this.services = [];
        this.integrations_error = api_error_message(error, 'Verification services could not be loaded.');
      } finally {
        this.loading = false;
      }
    },
    toggle_service(service = {}) {
      const service_id = positive_number(service.id);
      if (!service_id) return;

      const removing = this.service_is_selected(service);
      const updated_ids = removing
        ? this.selected_service_ids.filter((id) => id !== service_id)
        : [...this.selected_service_ids, service_id];

      try {
        this.selected_service_ids = saveTenantVerificationServiceIds(updated_ids, this.projectId);
        this.show_success(`${service.name} ${removing ? 'disabled' : 'enabled'} for verification.`);
      } catch (error) {
        this.integrations_error = api_error_message(error, 'The verification service selection could not be saved.');
      }
    },
  },
};
